import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabaseClient';
import { NotificationContainer } from '../../components/ui/NotificationToast';
import { DashboardSkeleton } from '../../components/ui/LoadingState';
import KPICard from './components/KPICard';
import AssetStatusChart from './components/AssetStatusChart';
import AssetCategoryChart from './components/AssetCategoryChart';
import AssetTrendChart from './components/AssetTrendChart';
import RecentActivityFeed from './components/RecentActivityFeed';
import QuickActions from './components/QuickActions';
import Button from '../../components/ui/Button';
import { useData } from '../../context/DataContext';
import { formatAssetStatus } from '../../utils/formatters';
import { calculateEOLDate } from '../../utils/assetUtils';

const Dashboard = () => {
  const { t } = useTranslation();
  const [isLoadingLocal, setIsLoadingLocal] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [dashboardCounts, setDashboardCounts] = useState({
    totalAssets: 0,
    inUseCount: 0,
    inStorageCount: 0,
    totalUsers: 0,
    pendingLoans: 0
  });

  const { assets, isLoading: isDataLoading, currentUser } = useData();
  const navigate = useNavigate();

  const isPic = currentUser?.role === 'pic' || currentUser?.role === 'department_pic';

  const realStats = useMemo(() => {
    if (!assets) return {
      totalAssets: 0,
      inUseCount: 0,
      inStorageCount: 0,
      expiringCount: 0,
      assetsByStatus: [],
      assetsByCategory: [],
      trendData: []
    };

    const filteredAssets = isPic && currentUser?.department_id 
      ? assets.filter(a => a.current_department_id === currentUser.department_id)
      : assets;

    const totalAssets = filteredAssets.length;
    const inUseCount = filteredAssets.filter(a => a.status === 'In Use').length;
    const inStorageCount = filteredAssets.filter(a => a.status === 'Available').length;
    
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const ninetyDaysFromNow = new Date(todayMidnight);
    ninetyDaysFromNow.setDate(todayMidnight.getDate() + 90);

    const expiringCount = filteredAssets.filter(a => {
      // Exclude decommissioned/dead assets
      if (['Broken', 'Stolen', 'Retired', 'Written-Off', 'Written Off', 'lost'].includes(a.status)) {
        return false;
      }
      const eolDate = calculateEOLDate(a.purchase_date, a.lifespan_years);
      return eolDate && eolDate <= ninetyDaysFromNow;
    }).length;

    const statusMap = filteredAssets.reduce((acc, curr) => {
      acc[curr.status] = (acc[curr.status] || 0) + 1;
      return acc;
    }, {});
    const assetsByStatus = Object.keys(statusMap).map(key => ({
      name: formatAssetStatus(key),
      value: statusMap[key],
      total: totalAssets
    }));

    const categoryMap = filteredAssets.reduce((acc, curr) => {
      acc[curr.category] = (acc[curr.category] || 0) + 1;
      return acc;
    }, {});
    const assetsByCategory = Object.keys(categoryMap).map(key => ({
      category: key,
      count: categoryMap[key]
    }));

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const today = new Date();
    const monthlyCounts = new Map();

    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthKey = `${monthNames[d.getMonth()]} ${d.getFullYear()}`;
      monthlyCounts.set(monthKey, 0);
    }

    filteredAssets.forEach(asset => {
      const assetDate = new Date(asset.created_at);
      const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 5, 1);
      if (assetDate >= sixMonthsAgo) {
        const monthKey = `${monthNames[assetDate.getMonth()]} ${assetDate.getFullYear()}`;
        if (monthlyCounts.has(monthKey)) {
          monthlyCounts.set(monthKey, monthlyCounts.get(monthKey) + 1);
        }
      }
    });

    const trendData = Array.from(monthlyCounts, ([month, count]) => ({ month, count }));

    return {
      totalAssets,
      inUseCount,
      inStorageCount,
      expiringCount,
      assetsByStatus,
      assetsByCategory,
      trendData,
    };
  }, [assets, isPic, currentUser]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoadingLocal(true);
      try {
        const deptId = currentUser.department_id;

        // Parallel Fetch: Fire all queries at once to eliminate waterfalls
        // Using { count: 'exact', head: true } to fetch ONLY the number of rows
        const queries = [
          supabase.from('assets').select('*', { count: 'exact', head: true }),
          supabase.from('assets').select('*', { count: 'exact', head: true }).eq('status', 'In Use'),
          supabase.from('assets').select('*', { count: 'exact', head: true }).eq('status', 'Available'),
          supabase.from('profiles').select('*', { count: 'exact', head: true }),
          supabase.from('loans').select('*', { count: 'exact', head: true }),
          supabase
            .from('activities')
            .select('*, assets ( product_name, asset_tag, current_department_id )')
            .order('created_at', { ascending: false })
            .limit(10)
        ];

        // Apply department filtering for PIC
        if (isPic && deptId) {
          queries[0] = queries[0].eq('current_department_id', deptId);
          queries[1] = queries[1].eq('current_department_id', deptId);
          queries[2] = queries[2].eq('current_department_id', deptId);
          // queries[3] (profiles) and queries[4] (loans) might also need filtering if applicable
        }

        const [
          { count: totalAssets },
          { count: inUseAssets },
          { count: availableAssets },
          { count: totalUsers },
          { count: totalLoans },
          { data: activitiesData, error: activitiesError }
        ] = await Promise.all(queries);

        if (activitiesError) throw activitiesError;

        setDashboardCounts({
          totalAssets: totalAssets || 0,
          inUseCount: inUseAssets || 0,
          inStorageCount: availableAssets || 0,
          totalUsers: totalUsers || 0,
          pendingLoans: totalLoans || 0
        });

        let filteredActivities = activitiesData || [];
        if (isPic) {
          filteredActivities = filteredActivities.filter(a => a.assets?.current_department_id === currentUser.department_id);
        }
        filteredActivities = filteredActivities.slice(0, 5);

        const userIds = [...new Set(filteredActivities.map(a => a.user_id).filter(Boolean))];

        let profilesMap = {};
        if (userIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, full_name, email')
            .in('id', userIds);

          if (!profilesError && profilesData) {
            profilesMap = profilesData.reduce((acc, profile) => {
              acc[profile.id] = profile;
              return acc;
            }, {});
          }
        }
        setRecentActivities(filteredActivities.map(activity => ({
          id: activity.id,
          type: activity.type,
          description: activity.description,
          user: profilesMap[activity.user_id] ? (profilesMap[activity.user_id].full_name || profilesMap[activity.user_id].email) : 'Unknown User',
          timestamp: new Date(activity.created_at),
          assetId: activity.assets?.asset_tag || activity.asset_tag,
        })));

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoadingLocal(false);
      }
    };

    if (currentUser) {
      fetchDashboardData();
    }
  }, [isPic, currentUser]);

  const kpiData = [
    {
      title: t('dashboard.total_assets'),
      value: (dashboardCounts.totalAssets || realStats.totalAssets).toLocaleString(),
      subtitle: isPic ? t('dashboard.your_department') : t('dashboard.active_inventory'),
      icon: "Package",
      color: "primary"
    },
    {
      title: t('dashboard.asset_in_use'),
      value: (dashboardCounts.inUseCount || realStats.inUseCount).toLocaleString(),
      subtitle: t('dashboard.currently_deployed'),
      icon: "CheckCircle",
      color: "success"
    },
    // Show In Storage only for non-PIC users
    ...(isPic ? [] : [{
      title: t('dashboard.in_storage'),
      value: (dashboardCounts.inStorageCount || realStats.inStorageCount).toLocaleString(),
      subtitle: t('dashboard.ready_for_deployment'),
      icon: "Archive",
      color: "warning"
    }]),
    {
      title: t('dashboard.expiring_soon'),
      value: realStats.expiringCount.toLocaleString(),
      subtitle: t('dashboard.within_90_days'),
      icon: "AlertTriangle",
      color: "error"
    }
  ];

  if (isDataLoading || isLoadingLocal) {
    return (
      <div>
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isPic ? t('dashboard.department_dashboard', { department: currentUser.department_name }) : t('dashboard.title')}
          </h1>
          <p className="text-muted-foreground">{t('dashboard.subtitle')}</p>
        </div>
        {!isPic && (
          <div className="flex flex-col sm:flex-row gap-3">
            <Button variant="outline" iconName="Upload" onClick={() => navigate('/asset-registration?mode=bulk')}>
              {t('dashboard.bulk_import')}
            </Button>
            <Button variant="default" iconName="Plus" onClick={() => navigate('/asset-registration')}>
              {t('dashboard.add_asset')}
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {kpiData?.map((kpi, index) =>
          <KPICard key={index} {...kpi} />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <AssetStatusChart data={realStats.assetsByStatus} />
        <AssetCategoryChart data={realStats.assetsByCategory} />
        <AssetTrendChart data={realStats.trendData} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <div className="lg:col-span-2">
          <RecentActivityFeed activities={recentActivities} />
        </div>
        {!isPic && (
          <div>
            <QuickActions />
          </div>
        )}
      </div>

      <NotificationContainer
        notifications={notifications}
        onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))}
      />
    </div>
  );
};

export default Dashboard;
